import { useState, useEffect } from 'react';
import { Lock, ArrowClockwise, X } from '@phosphor-icons/react';
import { useAuth } from '../middleware/authMiddleware';

const TokenExpiryPopup = () => {
  const { logout, login, refreshToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [username, setUsername] = useState('');
  const [authRole,setAuthRole] = useState('');

  useEffect(() => {
    setUserEmail(localStorage.getItem('user_email') || '');
    setUsername(localStorage.getItem('user_name') || '');
    setAuthRole(localStorage.getItem('auth_role'));
    
    const handleApiError = (event) => {
      if (event.detail?.status === 401) {
        setIsOpen(true);
      }
    };
    
    window.addEventListener('apiError', handleApiError);
    return () => window.removeEventListener('apiError', handleApiError);
  }, []);

  const handleRefresh = async (e) => {
    e.preventDefault();
    setIsRefreshing(true);
    setError('');
    
    try {
      // First try silent refresh
      const result = await refreshToken();
      if (result) {
        setIsOpen(false);
        return;
      }
      
      // If silent refresh failed, try with password
      if (!password) {
        throw new Error('Please enter your password');
      }
      
      const loginResponse = await login({
        username: username,
        password
      }, authRole);
      
      if (loginResponse.success) {
        setIsOpen(false);
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (err) {
      setError(err.message || 'Failed to refresh session');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Lock size={20} className="mr-2" />
            Session Expired
          </h3>
          <button onClick={handleLogout} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        
        <p className="text-gray-600 mb-4">
          Your session has expired. Please enter your password to continue.
        </p>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleRefresh}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email
            </label>
            <input
              type="text"
              value={username}
              readOnly
              className="w-full p-2 border border-gray-300 rounded bg-gray-100"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleLogout}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Logout
            </button>
            <button
              type="submit"
              disabled={isRefreshing}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
            >
              {isRefreshing ? (
                <ArrowClockwise size={18} className="animate-spin mr-2" />
              ) : null}
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TokenExpiryPopup;